import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import FourDigitCode from "./FourDigitCode";
import "./public.css";
import { IoArrowBackCircle, IoWarning } from "react-icons/io5";
import Cart from "../shop/cart/Cart";

export default function PublicCart() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const [passcode, setPasscode] = useState("");
    const [invalidPass, setInvalidPass] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [authorized, setAuthorized] = useState(false);
    const [tempToken, setTempToken] = useState("");
    const [passcodeRequired, setPasscodeRequired] = useState(null);
    const [isApproved, setIsApproved] = useState(false);
    let api = process.env.REACT_APP_API_URL;

    useEffect(() => {
        const checkPasscodeRequirement = async () => {
            try {
                const response = await fetch(api + "/public/check-for-passcode", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                    body: JSON.stringify({ projectId: id, hash: token }),
                });

                const data = await response.json();

                if (data.err) {
                    setError(data.err);
                    setPasscodeRequired(true);
                } else {
                    setPasscodeRequired(data.passcodeRequired);

                    if (!data.passcodeRequired) {
                        setAuthorized(true);
                        setTempToken(data.tempToken);
                        setIsApproved(data.isApproved);
                    }
                }
            } catch (err) {
                setError("Failed to check access requirements");
                setPasscodeRequired(true);
            } finally {
                setLoading(false);
            }
        };

        if (id && token) {
            checkPasscodeRequirement();
        }
    }, [id, token, api]);

    const submitCode = () => {
        setLoading(true);
        fetch(api + "/public/validate", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body: JSON.stringify({ projectId: id, hash: token, passcode }),
        })
            .then((res) => res.json())
            .then((data) => {
                if (data) {
                    if (data.err) {
                        setError(data.err);
                    } else {
                        setAuthorized(true);
                        setTempToken(data.tempToken);
                        setIsApproved(data.isApproved);
                    }
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    if (authorized && tempToken) {
        return (
            <div>
                <div className="flex items-center justify-start p-4 bg-gray-100 border-b border-gray-300">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white shadow hover:bg-gray-200 transition-colors border border-gray-300"
                    >
                        <IoArrowBackCircle className="text-2xl text-black" />
                        <span className="font-semibold text-gray-700">Go back</span>
                    </button>
                </div>
                <Cart isGuest={true} guestToken={tempToken} api={api} token={token} />
            </div>
        );
    }

    if (loading) {
        return (
            <div className="h-screen w-full flex justify-center items-center bg-black">
                <div className="flex flex-col ">
                    <div className="bg-white p-8  rounded-md shadow-lg">
                        <div className="flex gap-2 justify-center items-center mt-4">
                            <div className="dot"></div>
                            <div className="dot"></div>
                            <div className="dot"></div>
                        </div>
                        <p className="mt-5">{passcodeRequired === null ? "Checking access..." : "Validating..."}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (passcodeRequired) {
        return (
            <div className="h-screen w-full flex justify-center items-center bg-black">
                <div className="flex flex-col min-h-[30vh]">
                    <div className="bg-white p-8 md:max-w-[30vw] lg:max-w-[20vw] rounded-md shadow-lg">
                        <div className="flex justify-center items-center mt-2 mb-10">
                            <img src="https://rhino-public-resources.s3.us-west-1.amazonaws.com/logo-line.png" alt="logo" className="h-12 w-auto" />
                        </div>
                        <p>This is a password protected page.</p>
                        <div className="flex items-center gap-2 mb-8">
                            <p>Please enter the passcode:</p>
                        </div>
                        <FourDigitCode setCompletedCode={setPasscode} setIncomplete={setInvalidPass} />
                        <button
                            className={`${invalidPass ? "bg-gray-400" : "bg-green-500 hover:bg-green-600"} w-full text-white p-2 rounded-md mt-8`}
                            disabled={invalidPass}
                            onClick={submitCode}>
                            Enter Code
                        </button>
                        {error ? (
                            <div className="flex items-center gap-2 mt-2 bg-yellow-300 p-2 rounded-md">
                                <IoWarning className="text-3xl" />
                                <p>{error}</p>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        );
    } else {
        return null;
    }
}